import { configureStore } from '@reduxjs/toolkit'
import handleAnswers from './Reducer'

// Storage key for localStorage
const STORAGE_KEY = 'mchat-assessment-data';

// Migration function to convert old state structure to new one
const migrateState = (oldState: any) => {
  if (!oldState) return undefined;
  
  // If the state has the old structure with 'answers' and 'currentQuestionIndex'
  if (oldState.answers !== undefined || oldState.currentQuestionIndex !== undefined) {
    console.log('Migrating old state structure to new one...');
    
    // Extract only the fields we still use
    const newState = {
      questions: oldState.questions || {},
      childInfo: oldState.childInfo || null,
    };
    
    console.log('Migrated state:', newState);
    return newState;
  }
  
  // If it has the old questionResults array structure, migrate to new hierarchical structure
  if (oldState.questionResults && Array.isArray(oldState.questionResults)) {
    console.log('Migrating questionResults array to hierarchical structure...');
    
    const questions: { [key: string]: any } = {};
    
    oldState.questionResults.forEach((result: any) => {
      const questionId = `Q${result.questionId}`;
      
      if (result.questionId === 20) {
        // Handle Question 20's complex structure
        const questionNode: any = {
          id: questionId,
          answer: result.Q20 || null,
          result: result.result,
          completed: result.completed,
          userExample: result.userExample,
          noExamplesChecked: result.noExamplesChecked,
          examplesSaved: result.examplesSaved,
          userExampleAudio: result.userExampleAudio,
          children: []
        };
        
        // Add Q20.1
        if (result['Q20.1'] !== undefined) {
          questionNode.children!.push({
            id: 'Q20.1',
            answer: result['Q20.1']
          });
        }
        
        // Add Q20.2 with its children
        if (result['Q20.2.1'] !== undefined || result['Q20.2.2'] !== undefined || result['Q20.2.3'] !== undefined) {
          const q20_2: any = {
            id: 'Q20.2',
            answer: null,
            children: []
          };
          
          if (result['Q20.2.1'] !== undefined) {
            q20_2.children.push({
              id: 'Q20.2.1',
              answer: result['Q20.2.1']
            });
          }
          
          if (result['Q20.2.2'] !== undefined) {
            q20_2.children.push({
              id: 'Q20.2.2',
              answer: result['Q20.2.2']
            });
          }
          
          if (result['Q20.2.3'] !== undefined) {
            q20_2.children.push({
              id: 'Q20.2.3',
              answer: result['Q20.2.3']
            });
          }
          
          questionNode.children!.push(q20_2);
        }
        
        questions[questionId] = questionNode;
      } else {
        // Handle other questions with legacy structure
        const questionNode = {
          id: questionId,
          answer: result.mainAnswer || null,
          result: result.result,
          completed: result.completed,
          userExample: result.userExample,
          noExamplesChecked: result.noExamplesChecked,
          examplesSaved: result.examplesSaved,
          userExampleAudio: result.userExampleAudio,
          children: result.subAnswers ? result.subAnswers.map((answer: any, index: number) => ({
            id: `${questionId}.${index + 1}`,
            answer: answer
          })) : []
        };
        
        questions[questionId] = questionNode;
      }
    });
    
    const newState = {
      questions,
      childInfo: oldState.childInfo || null,
    };
    
    console.log('Migrated to hierarchical structure:', newState);
    return newState;
  }
  
  // If it's already the new structure, return as is
  return oldState;
};

// Load state from localStorage
const loadState = () => {
  try {
    const serializedState = localStorage.getItem(STORAGE_KEY);
    if (serializedState === null) {
      return undefined;
    }
    const parsed = JSON.parse(serializedState);
    
    // Migrate old state structure if needed
    const migratedState = migrateState(parsed);
    
    // If migration happened, save the new structure immediately
    if (migratedState && migratedState !== parsed) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(migratedState));
      console.log('Saved migrated state to localStorage');
    }
    
    return migratedState;
  } catch (err) {
    console.warn('Failed to load state from localStorage:', err);
    return undefined;
  }
};

// Save state to localStorage
const saveState = (state: any) => {
  try {
    const serializedState = JSON.stringify(state);
    localStorage.setItem(STORAGE_KEY, serializedState);
    //console.log('Saved state to localStorage:', state);
  } catch (err) {
    console.warn('Failed to save state to localStorage:', err);
  }
};

// Get persisted state
const persistedState = loadState();

// Create the store
const store = configureStore({
  reducer: handleAnswers as any,
  preloadedState: persistedState,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }),
});

// Subscribe to store updates to save to localStorage
store.subscribe(() => {
  const state = store.getState();
  saveState(state);
});

// Define RootState type to match the actual state structure
export type RootState = ReturnType<typeof handleAnswers>;
export type AppDispatch = typeof store.dispatch;

export default store